/**
 * cart-drawer-lazy.js
 *
 * Lazily loads the cart drawer's heavy content (recommendations, bundle
 * suggestions, attached add-ons, empty-cart pills) from the standalone
 * `cart-drawer-lazy` section via the Section Rendering API. The always-
 * rendered cart-drawer shell only contains placeholders:
 *
 *   [data-cart-drawer-lazy-part="empty-cart" | "recommendations" |
 *    "overlay" | "addon--<line item key>"]
 *
 * The fetched section contains matching [data-lazy-part="..."] elements which
 * are injected into those placeholders. Injection happens via innerHTML, so
 * inline <script> tags in the fetched HTML never execute — all behavior for
 * the injected content lives here (and in product-quick-overlay.js /
 * subscription-selector.js, which are delegation-based).
 *
 * Load/refresh policy:
 *  - The drawer (#cart-drawer, an Impact `Drawer` custom element) opens by
 *    getting an `open` attribute; a MutationObserver on that attribute
 *    triggers the first fetch (no theme.js changes needed).
 *  - cart:refresh / cart:change / variant:add mark the content dirty; the
 *    theme then re-renders the drawer shell (replaceChildren on #cart-drawer),
 *    which the same observer sees as a childList mutation and — if the drawer
 *    is open — triggers a re-fetch into the freshly rendered placeholders.
 */
(function () {
  'use strict';

  if (window.__cartDrawerLazyInit) return;
  window.__cartDrawerLazyInit = true;

  var SECTION_ID = 'cart-drawer-lazy';
  var TARGET_ATTR = 'data-cart-drawer-lazy-part';
  var PART_ATTR = 'data-lazy-part';

  var dirty = true;
  var fetching = false;
  var queued = false;
  var fetchToken = 0;
  var observedDrawer = null;
  var refetchTimer = null;

  /* One shopper action fires several cart events — an upsell added in the
     drawer, then the mystery gift that action pushed over the threshold. Each
     one used to cost a full render of this section. */
  var REFETCH_DELAY = 250;

  /* The part elements currently on the page, by name. theme.js re-renders the
     drawer shell on every cart change, which detaches them and puts the empty
     placeholders (and their skeletons) back; keeping the nodes means they can
     be moved straight back in, instead of the drawer flashing blank until a
     fresh section render arrives. */
  var injectedParts = {};

  // Backoff for failed loads. Consecutive failures walk down this list and then
  // stop; opening the drawer again or changing the cart resets the count.
  var RETRY_DELAYS = [600, 2000, 5000];
  var failures = 0;
  var retryTimer = null;

  function getDrawer() {
    return document.getElementById('cart-drawer');
  }

  function drawerIsOpen() {
    var drawer = getDrawer();
    return !!(drawer && drawer.hasAttribute('open'));
  }

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function replaceChildrenWith(target, node) {
    while (target.firstChild) target.removeChild(target.firstChild);
    if (node) target.appendChild(node);
  }

  /* ------------------------------------------------------------------ */
  /* Injection                                                          */
  /* ------------------------------------------------------------------ */

  function inject(html) {
    // A plain div + innerHTML keeps every <script> permanently inert
    // (do NOT switch to <template>: scripts moved out of template content
    // DO execute).
    var holder = document.createElement('div');
    holder.innerHTML = html;

    var content = holder.querySelector('[data-cart-drawer-lazy-content]');
    if (!content) return false;

    // Strip inert inline scripts (keep JSON data blocks).
    Array.prototype.forEach.call(content.querySelectorAll('script'), function (s) {
      var type = (s.getAttribute('type') || 'text/javascript').toLowerCase();
      if (type.indexOf('json') === -1) s.parentNode.removeChild(s);
    });

    // Inject the (static) style block once.
    var style = content.querySelector('style[data-cart-drawer-lazy-style]');
    if (style) {
      var existing = document.head.querySelector('style[data-cart-drawer-lazy-style]');
      if (!existing) {
        document.head.appendChild(style);
      } else {
        /* Replace rather than skip. Keeping the first copy forever meant a
           revalidated response — or a theme deploy mid-session — painted new
           markup against stale CSS. */
        if (existing.textContent !== style.textContent) existing.textContent = style.textContent;
        if (style.parentNode) style.parentNode.removeChild(style);
      }
    }

    /* Collect first, swap second. Clearing every placeholder up front — the way
       this used to start — emptied the drawer for the length of one parse on
       every re-render, which on a phone is the flash of blank boxes the
       recommendations used to be. A placeholder is now only emptied when the
       response genuinely has nothing for it (cart switched between empty and
       non-empty), never as a step on the way to filling it. */
    var parts = {};
    Array.prototype.forEach.call(content.querySelectorAll('[' + PART_ATTR + ']'), function (part) {
      parts[part.getAttribute(PART_ATTR)] = part;
    });

    injectedParts = {};
    Array.prototype.forEach.call(document.querySelectorAll('[' + TARGET_ATTR + ']'), function (target) {
      var name = target.getAttribute(TARGET_ATTR);
      var part = Object.prototype.hasOwnProperty.call(parts, name) ? parts[name] : null;
      replaceChildrenWith(target, part);
      if (part) injectedParts[name] = part;
    });

    return true;
  }

  /* Put the content that was already on screen back into the placeholders the
     shell re-render just restored. The nodes are still alive — only detached —
     so this is a move, not a parse: the shopper carries on looking at what they
     had while the fresh copy is fetched behind it. */
  function repaintInjectedParts() {
    var repainted = false;

    Object.keys(injectedParts).forEach(function (name) {
      var node = injectedParts[name];
      var target = document.querySelector('[' + TARGET_ATTR + '="' + name + '"]');
      if (!target || node.parentNode === target) return;
      replaceChildrenWith(target, node);
      repainted = true;
    });

    if (repainted) afterInject();
  }

  /* The shell hides an add-on's own line item because an attached card is
     meant to stand in for it. Whenever that card is not on the page — the
     section errored, the request failed, the product went unavailable — put the
     line item back. A hidden line item with nothing representing it means the
     customer is charged for something they cannot see or remove. */
  function reconcileAddonLineItems() {
    var rendered = {};
    Array.prototype.forEach.call(document.querySelectorAll('[data-cart-addon]'), function (card) {
      rendered[card.getAttribute('data-addon-handle')] = true;
    });

    Array.prototype.forEach.call(document.querySelectorAll('[data-addon-pending]'), function (el) {
      if (rendered[el.getAttribute('data-addon-pending')]) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    });
  }

  function restoreAllAddonLineItems() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-addon-pending]'), function (el) {
      el.removeAttribute('hidden');
    });
  }

  function afterInject() {
    initializeAddons();
    reconcileAddonLineItems();
    document.dispatchEvent(new CustomEvent('cart-drawer-lazy:loaded'));
  }

  /* ------------------------------------------------------------------ */
  /* Fetching                                                           */
  /* ------------------------------------------------------------------ */

  function maybeFetch(force) {
    if (force) dirty = true;
    if (!dirty) return;
    if (fetching) {
      queued = true;
      return;
    }
    doFetch();
  }

  /* Trailing debounce, so a burst of cart events costs one render instead of
     one each, and so the request goes out while the cart has settled. */
  function scheduleFetch() {
    if (refetchTimer) clearTimeout(refetchTimer);
    refetchTimer = setTimeout(function () {
      refetchTimer = null;
      maybeFetch(true);
    }, REFETCH_DELAY);
  }

  /* A failed load leaves the drawer showing line items and totals with a blank
     gap where the recommendations belong, so it has to retry — but on a backoff.
     Retrying immediately (which is what `dirty` in the finally block below used
     to do) turns one failure into a request loop that runs for as long as the
     drawer is open, and hammering the section endpoint is itself a good way to
     get thecomapny challenge page that caused the failure in the first place. */
  function scheduleRetry() {
    if (retryTimer || failures > RETRY_DELAYS.length) return;

    var delay = RETRY_DELAYS[Math.min(failures - 1, RETRY_DELAYS.length - 1)];
    retryTimer = setTimeout(function () {
      retryTimer = null;
      // Not gated on the drawer still being open: the content goes into
      // placeholders either way, so a shopper who closed the drawer while the
      // retry was pending finds it already filled when they open it again.
      maybeFetch(true);
    }, delay);
  }

  /* Both failure paths land here. The second one matters as much as the first:
     a 200 response that is not the section (Shopify's connection-check page, an
     error page, a redirect) used to leave `dirty` false, so nothing retried and
     the drawer stayed blank for the rest of the session. */
  function onFetchFailed() {
    failures++;
    dirty = true;
    restoreAllAddonLineItems();
    scheduleRetry();

    // Out of retries: drop the skeletons rather than shimmer forever at content
    // that is not coming.
    if (failures > RETRY_DELAYS.length) {
      Array.prototype.forEach.call(document.querySelectorAll('.cart-lazy-skeleton'), function (el) {
        el.parentNode && el.parentNode.removeChild(el);
      });
    }
  }

  function doFetch() {
    fetching = true;
    queued = false;
    dirty = false;
    var token = ++fetchToken;

    // Watchdog: a request that never settles must not leave add-on line items
    // hidden behind a card that is never coming.
    var watchdog = setTimeout(function () {
      if (token === fetchToken) restoreAllAddonLineItems();
    }, 6000);

    fetch(rootUrl() + '?section_id=' + SECTION_ID, {
      headers: { Accept: 'text/html' },
      cache: 'no-store'
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart-drawer-lazy fetch failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        if (token !== fetchToken) return;
        if (inject(html)) {
          failures = 0;
          writeCache(html);
          afterInject();
        } else {
          onFetchFailed(); // response had no usable content
        }
      })
      .catch(function () {
        if (token !== fetchToken) return;
        onFetchFailed();
      })
      .finally(function () {
        clearTimeout(watchdog);
        fetching = false;
        // Only a request that arrived while this one was in flight re-fetches
        // straight away; failures come back through scheduleRetry().
        if (queued && drawerIsOpen()) maybeFetch();
      });
  }

  /* ------------------------------------------------------------------ */
  /* Drawer open detection (no theme.js edits)                          */
  /* ------------------------------------------------------------------ */

  function observeDrawer() {
    var drawer = getDrawer();
    if (!drawer || drawer === observedDrawer) return;
    observedDrawer = drawer;

    new MutationObserver(function (mutations) {
      var opened = false;
      var reRendered = false;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].type === 'attributes') opened = true;
        else if (mutations[i].type === 'childList') reRendered = true;
      }
      if (!drawer.hasAttribute('open')) return;

      /* Watched down the whole subtree, not just the drawer's own children.
         theme.js re-renders the shell two different ways: it replaces the
         drawer's children, but for a cart:change — a line item removed, a
         quantity edited — it instead rewrites .cart-drawer__inner's innerHTML,
         and on a delay. That is a mutation one level down, so nothing here saw
         it: the content this file had already fetched and injected was wiped
         a second after it arrived, and the skeletons underneath it stayed for
         good. Which is the shape of the "removing an item leaves grey boxes"
         report. */
      if (reRendered) repaintInjectedParts();

      if (opened) {
        // Opening the drawer is the shopper asking for this content again, so a
        // previous run that exhausted its retries gets a fresh set.
        failures = 0;
        maybeFetch();
      }
    }).observe(drawer, {
      attributes: true,
      attributeFilter: ['open'],
      childList: true,
      subtree: true
    });
  }

  function onCartMutated() {
    dirty = true;
    clearCache();
    failures = 0; // a new cart is a new chance for a load that gave up
    observeDrawer(); // in case the drawer wasn't in the DOM at init time

    /* Start now rather than after theme.js has re-rendered the shell. Both are
       independent GETs, and waiting for that one put this section's render at
       the far end of a chain — the shopper paid for two round trips before the
       recommendations came back. Only worth spending a render on a drawer the
       shopper is looking at, or one already filled once. */
    if (drawerIsOpen() || Object.keys(injectedParts).length) scheduleFetch();
    // Otherwise the dirty flag stands and the next open picks it up.
  }

  /* Session cache.

     The fetch is the whole delay, and its answer barely changes between page
     views — so keep the last response and paint it immediately on the next page,
     then revalidate in the background. The drawer opens with content already in
     it instead of waiting a round trip.

     Keyed on what the cart actually holds (variant ids and quantities, straight
     out of the shell's own markup), so a cart that changed in another tab, at
     checkout, or on a previous page can never paint stale content. */
  var CACHE_KEY = 'sneak:cart-drawer-lazy:v1';

  function cartSignature() {
    var parts = [];
    Array.prototype.forEach.call(document.querySelectorAll('.cart-drawer__line-items [data-variant-id], .cart-drawer__line-items [data-line-item-key]'), function (el) {
      parts.push(el.getAttribute('data-variant-id') || el.getAttribute('data-line-item-key') || '');
    });

    if (!parts.length) {
      // Fall back to the visible item count, which the shell always renders.
      var count = document.querySelector('cart-count');
      parts.push(count ? count.textContent.trim() : '0');
    }

    return parts.join('|');
  }

  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return parsed && parsed.signature === cartSignature() ? parsed.html : null;
    } catch (error) {
      return null;
    }
  }

  function writeCache(html) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ signature: cartSignature(), html: html }));
    } catch (error) {
      /* private mode or quota — the cache is an optimisation, never a requirement */
    }
  }

  function clearCache() {
    try { sessionStorage.removeItem(CACHE_KEY); } catch (error) { /* nothing to clear */ }
  }

  /* Idle-time warm-up.

     The content is fetched on first open, which is why the drawer can sit empty
     for a moment: the request only starts once the shopper has already asked to
     see it. Starting it on idle instead means it is usually there before the
     drawer finishes opening.

     Only for a cart that has something in it. An empty cart is the common case
     for a browsing visitor, and there is no reason to spend a section render on
     someone who may never open the drawer at all. */
  function cartHasItems() {
    var lineItems = document.querySelector('.cart-drawer__line-items');
    return !!(lineItems && lineItems.children.length > 0);
  }

  function whenIdle(callback) {
    if ('requestIdleCallback' in window) requestIdleCallback(callback, { timeout: 3000 });
    else setTimeout(callback, 1200);
  }

  function prefetch() {
    if (!cartHasItems() || fetching || !dirty) return;
    doFetch();
  }

  function init() {
    observeDrawer();
    document.addEventListener('cart:refresh', onCartMutated);
    document.addEventListener('cart:change', onCartMutated);
    document.addEventListener('variant:add', onCartMutated);
    /* Cached answer for this exact cart: paint now, refresh after. */
    var cached = cartHasItems() ? readCache() : null;
    if (cached && inject(cached)) {
      afterInject();
      whenIdle(function () { maybeFetch(true); });
    } else if (drawerIsOpen()) {
      maybeFetch();
    } else if (document.readyState === 'complete') {
      whenIdle(prefetch);
    } else {
      window.addEventListener('load', function () { whenIdle(prefetch); }, { once: true });
    }

    /* Intent beats idle: a shopper reaching for the cart button gives us a couple
       of hundred milliseconds of head start, and covers the case where the idle
       callback has not run yet. */
    ['pointerenter', 'touchstart', 'focusin'].forEach(function (type) {
      document.addEventListener(type, function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        if (!target.closest('[aria-controls="cart-drawer"]')) return;
        prefetch();
      }, { capture: true, passive: true });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Bundle add form (ported from the inline script in                  */
  /* snippets/horizontal-product-bundle.liquid, which never executes    */
  /* when injected). Delegated + scoped to lazy-injected content.       */
  /* NOTE: the snippet's `quick-buy-drawer:add` listener was NOT ported */
  /* — nothing in the theme dispatches that event (dead code).          */
  /* ------------------------------------------------------------------ */

  function lazyContext(el) {
    return el.closest('[' + TARGET_ATTR + '] [' + PART_ATTR + ']') || el.closest('[' + PART_ATTR + ']');
  }

  async function handleBundleFormSubmit(form) {
    var get = function (name) {
      var input = form.querySelector('[name="' + name + '"]');
      return input ? input.value : '';
    };

    var bundleId = get('bundle_id');
    var bundleName = get('bundle_name');
    var bundleFinalPriceRaw = parseFloat(get('bundle_final_price'));
    var bundleFinalPrice = bundleFinalPriceRaw / 100.0; // Convert to decimal like bundle picker
    var mainVariantId = get('main_variant_id');
    var partnerVariantId = get('partner_variant_id');
    var mainProductHandle = get('main_product_handle');
    var partnerProductHandle = get('partner_product_handle');

    if (!bundleId || !mainVariantId || !partnerVariantId || !mainProductHandle || !partnerProductHandle) {
      console.error('Missing bundle information');
      return;
    }

    var context = lazyContext(form);
    var loadingText = (context && context.getAttribute('data-loading-text')) || 'Loading...';
    var addText = context && context.getAttribute('data-add-to-cart-text');

    var button = form.querySelector('button[type="button"]');
    var originalText = button ? button.textContent : '';
    if (button) {
      button.disabled = true;
      button.textContent = loadingText;
    }

    var restoreButton = function () {
      if (button) {
        button.disabled = false;
        button.textContent = addText || originalText;
      }
    };

    try {
      var cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) throw new Error('Failed to fetch cart');
      var cart = await cartResponse.json();
      if (!cart || !cart.items) throw new Error('Invalid cart data');

      var items = [];
      var bundleProperties = {
        _bundle: 'true',
        _bundle_id: bundleId,
        _bundle_title: bundleName,
        _bundle_final_price: bundleFinalPrice.toString()
      };

      var findInCart = function (variantId, handle) {
        return cart.items.find(function (item) {
          if (item.variant_id && parseInt(item.variant_id, 10) === parseInt(variantId, 10)) return true;
          if (item.product_handle && item.product_handle === handle) return true;
          return false;
        });
      };

      var mainInCart = findInCart(mainVariantId, mainProductHandle);
      var partnerInCart = findInCart(partnerVariantId, partnerProductHandle);

      var updatePromises = [];
      var queueUpdate = function (lineItem) {
        var updatedProperties = Object.assign({}, lineItem.properties || {}, bundleProperties);
        updatePromises.push(
          fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: lineItem.key, properties: updatedProperties })
          }).catch(function (error) {
            console.error('Error updating bundle product:', error);
            throw error;
          })
        );
      };

      // Only what this card newly adds is an upsell; a line the shopper already
      // had just joins the bundle and keeps its own attribution (or none).
      var addedProperties = Object.assign({}, bundleProperties, upsellAttribution(form));

      if (!mainInCart) items.push({ id: mainVariantId, quantity: 1, properties: addedProperties });
      else queueUpdate(mainInCart);

      if (!partnerInCart) items.push({ id: partnerVariantId, quantity: 1, properties: addedProperties });
      else queueUpdate(partnerInCart);

      if (updatePromises.length > 0) await Promise.all(updatePromises);

      if (items.length > 0) {
        var response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        });

        if (!response.ok) {
          var error = await response.json();
          console.error('Error adding bundle to cart:', error);
          restoreButton();
          return;
        }
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      restoreButton();
    }
  }

  // The _upsell_* line item properties for something added from inside `el`,
  // read off the nearest [data-upsell-*] markers (the same ones the size sheet
  // reads — see snippets/product-quick-overlay-js.liquid).
  function upsellAttribution(el) {
    var surface = el.closest('[data-upsell-source]');
    if (!surface) return {};

    var properties = { _upsell_source: surface.getAttribute('data-upsell-source') };
    ['reason', 'for'].forEach(function (key) {
      var holder = el.closest('[data-upsell-' + key + ']');
      if (holder && surface.contains(holder)) properties['_upsell_' + key] = holder.getAttribute('data-upsell-' + key);
    });
    return properties;
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[is="bundle-product-form"]')) return;
    // Only handle lazy-injected copies; a server-rendered copy elsewhere
    // would run its own inline script.
    if (!form.closest('[' + TARGET_ATTR + ']')) return;
    event.preventDefault();
    handleBundleFormSubmit(form);
  });

  // The form's CTA is rendered with type="button", which never fires a
  // submit event on its own (no bundle-product-form custom element exists) —
  // bridge clicks to requestSubmit() so the submit handler above runs.
  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;
    var button = event.target.closest('form[is="bundle-product-form"] button[type="button"]');
    if (!button || button.disabled) return;
    var form = button.closest('form[is="bundle-product-form"]');
    if (!form || !form.closest('[' + TARGET_ATTR + ']')) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  });

  /* ------------------------------------------------------------------ */
  /* Attached cart add-ons (snippets/cart-addon.liquid)                 */
  /*                                                                    */
  /* One card per matched line item, injected under it. Everything is   */
  /* delegated + per-card so several add-ons can coexist in one cart;   */
  /* only the card the section marked data-addon-auto-add="true" adds   */
  /* itself, the rest wait for a tap.                                   */
  /*                                                                    */
  /* The main cart page keeps its own server-rendered upsell            */
  /* (snippets/cart-upsell.liquid + its inline script) — different      */
  /* class, no overlap with the handlers below.                         */
  /* ------------------------------------------------------------------ */

  var ADDON_MIN_REQUEST_INTERVAL = 800;
  var addonLastRequestTime = 0;
  var addonBusy = false;
  // Handles this page-load already tried to auto-add, so a failed add (or a
  // stale section render) can never turn into an add-every-refresh loop.
  var addonAutoAdded = {};

  function addonStorageKey(handle) {
    return 'cart_addon_off_' + handle;
  }

  function addonIsDeclined(handle) {
    try {
      return (
        sessionStorage.getItem(addonStorageKey(handle)) === 'true' ||
        localStorage.getItem(addonStorageKey(handle)) === 'true'
      );
    } catch (e) {
      return false;
    }
  }

  function setAddonDeclined(handle, declined) {
    try {
      if (declined) {
        localStorage.setItem(addonStorageKey(handle), 'true');
        sessionStorage.setItem(addonStorageKey(handle), 'true');
      } else {
        localStorage.removeItem(addonStorageKey(handle));
        sessionStorage.removeItem(addonStorageKey(handle));
      }
    } catch (e) {
      /* storage unavailable */
    }
  }

  // Refuses a request while another one is in flight (or within the cooldown)
  // and puts the toggle back to whatever the cart actually holds, so the UI can
  // never claim an add that did not happen.
  function addonRefuse(card) {
    var now = Date.now();
    if (!addonBusy && now - addonLastRequestTime >= ADDON_MIN_REQUEST_INTERVAL) {
      addonLastRequestTime = now;
      return false;
    }
    syncAddonToggle(card);
    return true;
  }

  function syncAddonToggle(card) {
    var input = card.querySelector('.cart-addon__toggle-input');
    if (input) input.checked = card.getAttribute('data-addon-in-cart') === 'true';
  }

  function findAddonInCart(handle, variantId) {
    return fetch(rootUrl() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        return cart.items.find(function (item) {
          if (!item.properties || !item.properties._upsell_source) return false;
          // cart.js has exposed the product handle under both names over time.
          return (
            item.handle === handle ||
            item.product_handle === handle ||
            String(item.variant_id) === String(variantId)
          );
        });
      });
  }

  // how: 'auto' when the section pre-selected it, 'tap' when the shopper
  // switched it on — the difference between an upsell that sold and one that
  // was simply left in, so it goes on the order as _upsell_reason.
  function addAddonToCart(card, how) {
    if (addonRefuse(card)) return false;
    addonBusy = true;

    var handle = card.getAttribute('data-addon-handle');
    var variantId = card.getAttribute('data-addon-variant');

    fetch(rootUrl() + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: {
          _upsell_source: 'cart-drawer-addon',
          _upsell_reason: how === 'auto' ? 'addon-auto-added' : 'addon-tapped',
          _upsell_for: card.getAttribute('data-addon-for-title') || card.getAttribute('data-addon-for') || ''
        }
      })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart/add.js ' + response.status);
        card.setAttribute('data-addon-in-cart', 'true');
        card.classList.add('cart-addon--in-cart');
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      })
      .catch(function () {
        var input = card.querySelector('.cart-addon__toggle-input');
        if (input) input.checked = false;
        // Let a later tap retry.
        delete addonAutoAdded[handle];
      })
      .finally(function () {
        addonBusy = false;
      });

    return true;
  }

  function removeAddonFromCart(card) {
    if (addonRefuse(card)) return false;
    addonBusy = true;

    var handle = card.getAttribute('data-addon-handle');

    findAddonInCart(handle, card.getAttribute('data-addon-variant'))
      .then(function (item) {
        if (!item) return;
        return fetch(rootUrl() + 'cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ id: item.key, quantity: 0 })
        }).then(function (response) {
          if (!response.ok) throw new Error('cart/change.js ' + response.status);
          card.setAttribute('data-addon-in-cart', 'false');
          card.classList.remove('cart-addon--in-cart');
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        });
      })
      .catch(function () {
        var input = card.querySelector('.cart-addon__toggle-input');
        if (input) input.checked = true;
      })
      .finally(function () {
        addonBusy = false;
      });

    return true;
  }

  function initializeAddons() {
    var cards = document.querySelectorAll('[data-cart-addon]');

    Array.prototype.forEach.call(cards, function (card) {
      var handle = card.getAttribute('data-addon-handle');
      var input = card.querySelector('.cart-addon__toggle-input');
      if (!handle || !input) return;

      if (card.getAttribute('data-addon-in-cart') === 'true') {
        input.checked = true;
        return;
      }

      input.checked = false;

      if (card.getAttribute('data-addon-auto-add') !== 'true') return;
      if (addonIsDeclined(handle) || addonAutoAdded[handle]) return;

      input.checked = true;
      // Only burn the once-per-page guard if the request actually went out.
      if (addAddonToCart(card, 'auto')) addonAutoAdded[handle] = true;
      else input.checked = false;
    });
  }

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.matches || !target.matches('.cart-addon__toggle-input')) return;

    var card = target.closest('[data-cart-addon]');
    if (!card) return;

    var handle = card.getAttribute('data-addon-handle');

    // Only remember the preference if the request actually went out — a tap
    // refused mid-flight must not silently change what the customer gets.
    if (target.checked) {
      if (addAddonToCart(card, 'tap')) setAddonDeclined(handle, false);
    } else {
      if (removeAddonFromCart(card)) setAddonDeclined(handle, true);
    }
  });

  /* ------------------------------------------------------------------ */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
